import { gql, GraphQLClient } from "graphql-request";
import { createGraphqlClient, type GraphqlRequestConfig } from "./client";

export interface GraphqlPracticalChapterCallerOptions
  extends GraphqlRequestConfig {
  client?: GraphQLClient;
}

function ensureClient(options?: GraphqlPracticalChapterCallerOptions) {
  if (options?.client) {
    return options.client;
  }
  return createGraphqlClient(options);
}

// ==================== Types ====================

export interface GraphqlPracticalChapter {
  id: string;
  projectId: string;
  chapterNum: number;
  content: string;
  updatedAt: string | null;
}

export interface GraphqlPracticalChapterRevision {
  id: string;
  projectId: string;
  chapterNum: number;
  content: string;
  updatedBy: string | null;
  createdAt: string;
}

export interface UpdatePracticalChapterInput {
  projectId: string;
  chapterNum: number;
  content: string;
}

// ==================== Mutations ====================

const UPDATE_PRACTICAL_CHAPTER_MUTATION = `
  mutation UpdatePracticalChapter(
    $projectId: String!
    $chapterNum: Int!
    $content: String!
  ) {
    updatePracticalChapter(
      projectId: $projectId
      chapterNum: $chapterNum
      content: $content
    ) {
      id
      projectId
      chapterNum
      content
      updatedAt
    }
  }
`;

export async function updatePracticalChapter(
  input: UpdatePracticalChapterInput,
  callerOptions?: GraphqlPracticalChapterCallerOptions
): Promise<GraphqlPracticalChapter> {
  const client = ensureClient(callerOptions);

  const data = await client.request<{
    updatePracticalChapter: GraphqlPracticalChapter;
  }>(UPDATE_PRACTICAL_CHAPTER_MUTATION, input);

  return data.updatePracticalChapter;
}

// ==================== Revisions (編集履歴) ====================

const LIST_PRACTICAL_CHAPTER_REVISIONS_QUERY = gql`
  query PracticalChapterRevisions(
    $projectId: String!
    $chapterNum: Int!
    $limit: Int
  ) {
    practicalChapterRevisions(
      projectId: $projectId
      chapterNum: $chapterNum
      limit: $limit
    ) {
      id
      projectId
      chapterNum
      content
      updatedBy
      createdAt
    }
  }
`;

const GET_PRACTICAL_CHAPTER_REVISION_QUERY = gql`
  query PracticalChapterRevision($id: String!) {
    practicalChapterRevision(id: $id) {
      id
      projectId
      chapterNum
      content
      updatedBy
      createdAt
    }
  }
`;

const RESTORE_PRACTICAL_CHAPTER_REVISION_MUTATION = gql`
  mutation RestorePracticalChapterRevision($revisionId: String!) {
    restorePracticalChapterRevision(revisionId: $revisionId) {
      id
      projectId
      chapterNum
      content
      updatedBy
      createdAt
      updatedAt
    }
  }
`;

/**
 * 章本文の履歴一覧を取得 (新しい順)
 */
export async function listPracticalChapterRevisions(
  projectId: string,
  chapterNum: number,
  limit?: number,
  options?: GraphqlPracticalChapterCallerOptions
): Promise<GraphqlPracticalChapterRevision[]> {
  const client = ensureClient(options);
  const data = await client.request(LIST_PRACTICAL_CHAPTER_REVISIONS_QUERY, {
    projectId,
    chapterNum,
    limit: limit ?? null,
  });
  return (data as any).practicalChapterRevisions ?? [];
}

/**
 * 章本文の履歴 1 件を取得
 */
export async function getPracticalChapterRevision(
  id: string,
  options?: GraphqlPracticalChapterCallerOptions
): Promise<GraphqlPracticalChapterRevision | null> {
  const client = ensureClient(options);
  const data = await client.request(GET_PRACTICAL_CHAPTER_REVISION_QUERY, {
    id,
  });
  return (data as any).practicalChapterRevision ?? null;
}

/**
 * 履歴を本体に書き戻す（戻す前の状態は新しい revision として積まれる）
 */
export async function restorePracticalChapterRevision(
  revisionId: string,
  options?: GraphqlPracticalChapterCallerOptions
): Promise<GraphqlPracticalChapter> {
  const client = ensureClient(options);
  const data = await client.request(RESTORE_PRACTICAL_CHAPTER_REVISION_MUTATION, {
    revisionId,
  });
  return (data as any).restorePracticalChapterRevision;
}
